import { IBasketProductItem, IBranchData, ICustomerData } from "@/entities";
import {
    IOrder,
    Order,
    OrderError,
    PreOrderType,
    ShippingMode,
    ShippingType,
} from "@/entities/order/order";
import {
    IOrderProductItem,
    OrderProductItem,
} from "@/entities/order/orderProductItem";
import {
    IOrderScheduleData,
    OrderSchedule,
} from "@/entities/order/orderSchedule";
import { IOrderShipping } from "@/entities/order/orderShipping";
import { PreOrder } from "@/entities/preorder/preorder";
import { Address, IAddress } from "@/entities/user/address";
import { Module } from "vuex";
import {
    IActions,
    ICheckoutState,
    IGetters,
    IMutations,
} from "./checkout.type";
import dayjs from "dayjs";
import { IRequestToPayData } from "@/entities/requestToPay";
import { Auth } from "aws-amplify";

export const getters: IGetters = {
    productList: (state) => {
        return state.scheduleList.reduce<IOrderProductItem[]>((acc, s) => {
            if (s.productList) {
                return acc.concat(s.productList);
            }
            return acc;
        }, []);
    },
    totalItem: (state, getters) => {
        return (getters["productList"] as IOrderProductItem[]).reduce<number>(
            (acc: number, p: IOrderProductItem) => {
                return acc + p.quantity!;
            },
            0
        );
    },
};

export const mutations: IMutations = {
    setLoading: (state, payload) => {
        state.loading = payload;
    },
    setOwnerId: (state, payload) => {
        state.ownerId = payload;
    },
    setShippingType: (state, payload) => {
        state.shippingType = payload;
    },
    setShippingMode: (state, payload) => {
        state.shippingMode = payload;
    },
    setShippingAddress: (state, payload) => {
        state.shippingAddress = payload;
    },
    setNoPlasticBags: (state, payload) => {
        state.noPlasticBags = payload;
    },
    setRequestSubstitute: (state, payload) => {
        state.requestSubstitute = payload;
    },
    setSpecialComment: (state, payload) => {
        state.specialComment = payload;
    },
    setCouponCode: (state, payload) => {
        state.couponCode = payload;
    },
    setVoucherId: (state, payload) => {
        state.voucherId = payload;
    },
    setDeliveryTypeLoading: (state, payload) => {
        state.deliveryTypeLoading = payload;
    },
    setBranchList: (state, payload) => {
        state.branchList = payload;
    },
    setCanRegular: (state, payload) => {
        state.canRegular = payload;
    },
    setCanExpress: (state, payload) => {
        state.canExpress = payload;
    },
    setCanNationwide: (state, payload) => {
        state.canNationwide = payload;
    },
    setScheduleList: (state, payload) => {
        state.scheduleList = payload;
    },
    setDeliverySlot: (state, payload) => {
        console.log("setDeliverySlot", payload);
        for (let index = 0; index < state.scheduleList.length; index++) {
            if (state.scheduleList[index].scheduleId == payload.scheduleId) {
                state.scheduleList[index].booking_hour = payload.booking_hour;
                state.scheduleList[index].date_slot = payload.date_slot;
                state.scheduleList[index].dateTime = payload.dateTime;
            }
        }
        const copy = state;
        state = copy;
    },
    setCouponCodeList: (state, payload) => {
        state.couponCodeList = payload;
    },
    setCostLoading: (state, payload) => {
        state.costLoading = payload;
    },
    setCost: (state, payload) => {
        state.subTotal = payload.subTotal || null;
        state.grandTotal = payload.grandTotal || null;
        state.deliveryFee = payload.deliveryFee || null;
        state.expressFee = payload.expressShippingCost || null;
        state.discountTotal =
            (payload.cartDiscount || 0) + (payload.bogoDiscount || 0) + (payload.voucherDiscount || 0);
        state.promotionalDiscount = (payload.cartDiscount || 0) + (payload.bogoDiscount || 0)
        state.voucherDiscount = (payload.voucherDiscount || 0)
    },
    setPlaceOrderLoading: (state, payload) => {
        state.placeOrderLoading = payload;
    },
    // TODO tax invoice
};

export const actions: IActions = {
    async syncDraftOrder(this, context, payload): Promise<void> {
        try {
            context.commit("setLoading", true);

            console.log("syncDraftOrder payload:", payload);

            const shippingType = ShippingType.DELIVERY;
            const scheduleList: any[] = [];

            // TODO sync draft order
            const result = await this.$services.draftOrders.sync({
                ownerId: payload.id!,
                shipping: {
                    shippingType,
                    scheduleList,
                },
            });

            console.log({ result });

            // clear schedule list
            context.commit("setScheduleList", []);

            if (result.ownerId) {
                context.commit("setOwnerId", result.ownerId);
            }

            if (result.shipping) {
                // load shipping type to store
                if (result.shipping.shippingType) {
                    context.commit(
                        "setShippingType",
                        result.shipping.shippingType
                    );
                } else {
                    console.warn(
                        "shipping type not found, use default type 'DELIVERY'"
                    );
                    context.commit("setShippingType", ShippingType.DELIVERY);
                }

                if (result.shipping.shippingAddress) {
                    const address: IAddress = {
                        firstname: result.shipping.shippingFirstName,
                        lastname: result.shipping.shippingLastName,
                        address: result.shipping.shippingAddress,
                        district: result.shipping.shippingDistrict,
                        subDistrict: result.shipping.shippingSubDistrict,
                        province: result.shipping.shippingProvince,
                        postcode: result.shipping.shippingPostcode,
                        phone: result.shipping.shippingPhone,
                        email: result.shipping.shippingEmail,
                        location: {
                            lat: result.shipping.shippingLat,
                            lng: result.shipping.shippingLon,
                        },
                    };
                    context.commit("setShippingAddress", address);
                } else {
                    // use customer address at index 0
                    if (payload.address[0]) {
                        const addr = payload.address[0];
                        const address: IAddress = {
                            firstname: addr.firstname,
                            lastname: addr.lastname,
                            address: addr.address,
                            district: addr.district,
                            subDistrict: addr.subDistrict,
                            province: addr.province,
                            postcode: addr.postcode,
                            phone: addr.phone,
                            email: addr.email,
                            location: {
                                lat: addr.location?.lat,
                                lng: addr.location?.lng,
                            },
                        };
                        context.commit("setShippingAddress", address);
                    }
                }
            } else {
                console.warn("shipping not found");

                // load default shipping
                context.commit("setShippingType", ShippingType.DELIVERY);
            }

            if (
                result.noPlasticBag != null ||
                result.noPlasticBag != undefined
            ) {
                context.commit("setNoPlasticBags", result.noPlasticBag);
            } else {
                console.warn("no plastic bags not found");

                // set default
                context.commit("setNoPlasticBags", false);
            }

            if (
                result.requestSubstitute != null ||
                result.requestSubstitute != undefined
            ) {
                context.commit(
                    "setRequestSubstitute",
                    result.requestSubstitute
                );
            } else {
                console.warn("request substitude not found");

                // set default
                context.commit("setRequestSubstitute", false);
            }

            if (result.specialComment) {
                context.commit("setSpecialComment", result.specialComment);
            } else {
                // set default
                context.commit("setSpecialComment", "");
            }

            if (result.promotionCode) {
                context.commit("setCouponCode", result.promotionCode);
            } else {
                // set default
                context.commit("setCouponCode", undefined);
            }

            // clear schedule

            // TODO commit
            // context.commit("setCost", result);
        } catch (error) {
            console.warn(error);
        } finally {
            context.commit("setLoading", false);
        }
    },
    async updateDraftOrder(this, context, payload): Promise<void> {
        // TODO update draft order
        try {
            const shippingType = context.state.shippingType!;
            const shippingAddress = context.state.shippingAddress;
            const scheduleList: any[] = [];

            const shipping: IOrderShipping = {
                scheduleList,
                shippingType,
                shippingFirstName: shippingAddress?.firstname,
                shippingLastName: shippingAddress?.lastname,
                shippingAddress: shippingAddress?.address,
                shippingDistrict: shippingAddress?.district,
                shippingSubDistrict: shippingAddress?.subDistrict,
                shippingProvince: shippingAddress?.province,
                shippingPostcode: shippingAddress?.postcode,
                shippingPhone: shippingAddress?.phone,
                shippingEmail: shippingAddress?.email,
                shippingLat: shippingAddress?.location?.lat,
                shippingLon: shippingAddress?.location?.lng,
            };

            this.$services.draftOrders.update({
                ownerId: context.state.ownerId!,
                shipping,
                requestSubstitute: context.state.requestSubstitute,
                noPlasticBag: context.state.noPlasticBags,
                specialComment: context.state.specialComment as any,
                promotionCode: context.state.couponCode as any,
            });
        } catch (error) {
            console.warn("updateDraftOrder error:", error);
        }
    },
    setShippingType(this, context, payload): void {
        context.commit("setShippingType", payload);
        switch (payload) {
            case ShippingType.DELIVERY:
                {
                    // use customer address as shipping address
                    console.log("use customer address as shipping address");
                    const addressList: IAddress[] = (context.rootState as any)[
                        "activeCustomer"
                    ]["customer"]["address"];
                    console.log({ addressList });

                    if (addressList.length > 0) {
                        console.log(
                            "use customer address (index 0) as shipping address"
                        );
                        const address = new Address(addressList[0]);
                        context.commit("setShippingAddress", address.data());
                    }
                }
                break;
            case ShippingType.PICKUP:
                {
                    // use branch address as shipping address
                    console.log("use branch address as shipping address");
                    const branch = (context.rootState as any)["branch"][
                        "branch"
                    ];
                    const customer = (context.rootState as any)[
                        "activeCustomer"
                    ]["customer"];
                    const address = new Address({
                        name: branch.name,
                        firstname: customer.firstname,
                        lastname: customer.lastname,
                        address: branch.address,
                        district: branch.district,
                        subDistrict: branch.subDistrict,
                        province: branch.province,
                        postcode: branch.postcode,
                        phone: customer.phone_number,
                        email: customer.email,
                        location: {
                            lat: branch.latlng[0],
                            lng: branch.latlng[1],
                        },
                    });
                    context.commit("setShippingAddress", address.data());
                }
                break;
            default:
                break;
        }
    },
    async loadPolygon(this, context, payload): Promise<void> {
        console.log("loadPolygon");
        try {
            context.commit("setDeliveryTypeLoading", true);
            const apikey = await this.$services.authentication.getApikey();
            if (apikey) {
                const customer = (context.rootState as any)["activeCustomer"];
                if (customer) {
                    if (customer["customer"]) {
                        if (customer["customer"]["address"]) {
                            if (customer["customer"]["address"].length > 0) {
                                const address =
                                    customer["customer"]["address"][0];

                                const result =
                                    await this.$services.polygon.getDeliveryCost(
                                        address.location,
                                        apikey.key!
                                    );

                                let includeHQ = false;
                                for (
                                    let index = 0;
                                    index < result.length;
                                    index++
                                ) {
                                    if (result[index].eligible_to_shop_at_33) {
                                        includeHQ = true;
                                        break;
                                    }
                                }

                                if (includeHQ) {
                                    const b33 = result.findIndex(
                                        (b) => b.br_code === 1000
                                    );

                                    if (b33 < 0) {
                                        result.push({
                                            isInPolygon: true,
                                            br_code: 1000,
                                            store_name: "Sukhumvit 33",
                                            dist: 0,
                                            lat: 13.73204799,
                                            long: 100.56762289,
                                            dur: 0,
                                            cost: 0,
                                            eligible_to_shop_at_33: true,
                                        });
                                    }
                                }

                                context.commit("setBranchList", result);
                                if (result.length == 0) {
                                    // nationwide only
                                    context.commit("setCanRegular", false);
                                    context.commit("setCanExpress", false);
                                    context.commit("setCanNationwide", true);

                                    // set mode
                                    context.commit(
                                        "setShippingMode",
                                        ShippingMode.NATIONWIDE
                                    );
                                } else {
                                    // regular
                                    context.commit("setCanRegular", true);
                                    context.commit("setCanExpress", true);
                                    context.commit("setCanNationwide", false);

                                    // set mode
                                    context.commit(
                                        "setShippingMode",
                                        ShippingMode.REGULAR
                                    );
                                }
                            }
                        }
                    }
                }
            }
        } catch (error) {
            console.warn(error);
        } finally {
            context.commit("setDeliveryTypeLoading", false);
        }
        return;
    },
    async updateShippingAddress(this, context, payload) {
        console.log("updateShippingAddress: ", payload);

        if (payload.length > 0) {
            console.log(
                "use customer address (index 0) as shipping address"
            );
            const address = new Address(payload[0]);
            console.log("updateShippingAddress address: ", address.data());

            context.commit("setShippingAddress", address.data());
        }
    },
    computeScheduleGroup(this, context, payload): void {
        console.log("computeScheduleGroup", payload);

        const branch = (context.rootState as any)["branch"][
            "branch"
        ] as IBranchData;
        const branchCode = parseInt(branch.code);

        const productList = payload.map((p: IBasketProductItem) => {
            const orderItem: OrderProductItem = {
                productName: p.product.hemaNameEn!,
                productBrand: p.product.hemaBrandEn,
                quantity: p.basketItem.quantity,
                cprcode: p.product.cprcode,
                isPreOrder: false,
                originalPrice: p.product.originalPriceAt(branchCode),
                price: p.product.priceAt(branchCode),
            };

            if (p.product.preOrder) {
                orderItem.isPreOrder = true;
            }

            return orderItem;
        });

        console.log({ productList });

        const regularList: OrderProductItem[] = productList.filter(
            (product: OrderProductItem) => {
                if (product.isPreOrder) {
                    return false;
                }
                return true;
            }
        );

        console.log({ regularList });

        let index = 0;
        let os: OrderSchedule[] = [];

        const preorderList = productList.filter((product: OrderProductItem) => {
            if (product.isPreOrder) {
                return true;
            }
            return false;
        });

        console.log({ preorderList });

        if (regularList.length > 0) {
            const regularSchedule: OrderSchedule = new OrderSchedule({
                scheduleId: index,
                mode: ShippingMode.REGULAR,
                productList: [],
            });

            regularList.map((p) => {
                p.scheduleId = index;
            });

            regularSchedule.productList = regularList;

            console.log({ regularSchedule });

            regularSchedule.displayGroupName = "Regular";
            regularSchedule.startDate = dayjs().toISOString();

            os.push(regularSchedule);
            index++;
        }

        if (preorderList.length > 0) {
            const preorderGroups: OrderSchedule[] = preorderList.reduce(
                (previousValue: OrderSchedule[], product) => {
                    const p = this.$services.catalog.productMap?.get(
                        product.cprcode
                    );
                    if (p) {
                        const po = p.preOrder;
                        if (po) {
                            switch (po.preType) {
                                case "Relative":
                                    {
                                        const g: OrderSchedule | undefined =
                                            previousValue.find(
                                                (i: OrderSchedule) => {
                                                    i.preOrder ==
                                                        "PREORDER_RELATIVE" &&
                                                        i.preRelativeDay ==
                                                        po.preRelativeDay;
                                                }
                                            );

                                        if (g == null || g == undefined) {
                                            const gg: OrderSchedule =
                                                new OrderSchedule({
                                                    scheduleId: index,
                                                    mode: ShippingMode.REGULAR,
                                                    productList: [],
                                                });

                                            gg.displayGroupName = "Pre-order";
                                            gg.preRelativeDay =
                                                po.preRelativeDay;
                                            gg.startDate = dayjs()
                                                .add(po.preRelativeDay!, "day")
                                                .toISOString();
                                            gg.productList = [product];
                                            previousValue.push(gg);
                                            index++;
                                            return previousValue;
                                        }

                                        product.scheduleId = index;
                                        if (g.productList) {
                                            g.productList.push(product);
                                        }
                                    }
                                    break;
                                case "Fixed":
                                    {
                                        const g: OrderSchedule | undefined =
                                            previousValue.find(
                                                (i: OrderSchedule) => {
                                                    i.preOrder ==
                                                        "PREORDER_FIXED" &&
                                                        i.preRelativeDay ==
                                                        po.preRelativeDay;
                                                }
                                            );

                                        if (g == null || g == undefined) {
                                            const gg: OrderSchedule =
                                                new OrderSchedule({
                                                    scheduleId: index,
                                                    mode: ShippingMode.REGULAR,
                                                    productList: [],
                                                });

                                            gg.displayGroupName = "Pre-order";
                                            gg.deliveryDate = po.deliveryDate;
                                            gg.startDate = dayjs()
                                                .add(po.preRelativeDay!, "day")
                                                .toISOString();
                                            gg.productList = [product];
                                            previousValue.push(gg);
                                            index++;
                                            return previousValue;
                                        }

                                        product.scheduleId = index;
                                        if (g.productList) {
                                            g.productList.push(product);
                                        }
                                    }
                                    break;
                            }
                        }
                    }

                    return previousValue;
                },
                []
            );

            console.log({ preorderGroups });

            os = os.concat(preorderGroups);
        }

        context.commit("setScheduleList", os);
    },
    // TODO tax invoice actions
    async getCost(this, context, payload): Promise<boolean> {
        try {
            context.commit("setCostLoading", true);
            // get apikey
            const apikey = await this.$services.authentication.getApikey();

            const branch = (context.rootState as any)["branch"];

            const addr = context.state.shippingAddress;

            const scheduleList: OrderSchedule[] =
                context.state.scheduleList.map((s) => {
                    return new OrderSchedule(s);
                });

            // override
            console.log(
                context.state.shippingMode,
                context.state.shippingMode === ShippingMode.NATIONWIDE
            );
            if (context.state.shippingMode === ShippingMode.NATIONWIDE) {
                for (let index = 0; index < scheduleList.length; index++) {
                    scheduleList[index].mode = ShippingMode.NATIONWIDE;
                }
            }

            if (context.state.shippingMode === ShippingMode.EXPRESS) {
                for (let index = 0; index < scheduleList.length; index++) {
                    if (scheduleList[index].preOrder == undefined) {
                        scheduleList[index].mode = ShippingMode.EXPRESS;
                    }
                }
            }
            console.log({ scheduleList });

            const shipping: IOrderShipping = {
                scheduleList,
                shippingAddress: addr?.address,
                shippingSubDistrict: addr?.subDistrict || "",
                shippingDistrict: addr?.district || "",
                shippingProvince: addr?.province || "",
                shippingPostcode: addr?.postcode,
                shippingPhone: addr?.phone,
                shippingEmail: addr?.email,
                shippingFirstName: addr?.firstname,
                shippingLastName: addr?.lastname,
                shippingLat: addr?.location?.lat,
                shippingLon: addr?.location?.lng,
                shippingType: context.state.shippingType?.toString(),
            };

            const couponCodeList = [];
            if (context.state.couponCode) {
                couponCodeList.push(context.state.couponCode);
            }

            const customer: ICustomerData = (context.rootState as any)[
                "activeCustomer"
            ]["customer"];

            let ownerId;
            if (customer.phone_number) {
                ownerId = customer.phone_number;
            } else if (customer.email) {
                ownerId = customer.email;
            }

            // create order class
            const order = new Order({
                ownerId,
                branchId: branch["branch"].code,
                productList: context.getters["productList"],
                shipping,
                requestSubstitute: context.state.requestSubstitute,
                noPlasticBag: context.state.noPlasticBags,
                specialComment: context.state.specialComment || "",
                couponCodeList,
            });

            const response = await this.$services.calculatePrice.suggestCoupon(
                order,
                apikey!.key!
            );
            order.couponCodeList = response.calculateCostInput.couponCodeList;

            context.commit("setCouponCodeList", order.couponCodeList);

            if (context.state.couponCode) {
                order.couponCodeList.push(context.state.couponCode);
            }

            if (context.state.voucherId) {
                order.voucherId.push(...context.state.voucherId);
            }

            // resolve coupon
            const result = await this.$services.calculatePrice.getResolveCoupon(
                order,
                apikey!.key!
            );

            console.log({ result });
            context.commit("setCost", result);
            return true
        } catch (error) {
            console.warn(error);
            return false
        } finally {
            context.commit("setCostLoading", false);
        }
    },
    async validate(this, context, payload): Promise<void> {
        console.log("validate");

        // validate shipping type
        if (
            context.state.shippingType == null ||
            context.state.shippingType == undefined
        ) {
            throw Error(OrderError.SHIPPING_TYPE_ERROR);
        }

        // validate shipping mode
        if (context.state.shippingType === ShippingType.DELIVERY) {
            if (
                context.state.shippingMode == null ||
                context.state.shippingMode == undefined
            ) {
                throw Error(OrderError.DELIVERY_MODE_ERROR);
            }
        }

        // validate product list
        if ((context.getters.productList as IOrderProductItem[]).length <= 0) {
            throw Error(OrderError.NO_PRODUCT);
        }

        // TODO validate shipping address

        // validate schedule
        const scheduleList = context.state.scheduleList;
        for (let index = 0; index < scheduleList.length; index++) {
            const s = scheduleList[index];
            if (s.dateTime == null || s.dateTime == undefined) {
                throw new Error(OrderError.SCHEDULE_DATE_TIME_ERROR);
            }
        }

        console.log("validation: PASS");
    },
    async placeOrder(this, context, payload): Promise<IRequestToPayData> {
        try {
            context.commit("setPlaceOrderLoading", true);

            // TODO create order
            const customer: ICustomerData = (context.rootState as any)[
                "activeCustomer"
            ]["customer"];

            console.log(context.rootState, customer);

            // get idToken
            const idToken =
                await this.$services.authentication.getCustomerIdToken(
                    customer
                );

            const branch = (context.rootState as any)["branch"];

            const addr = context.state.shippingAddress;

            const scheduleList: OrderSchedule[] =
                context.state.scheduleList.map((s) => {
                    return new OrderSchedule(s);
                });

            console.log("scheduleList:", scheduleList);

            console.log(
                context.state.shippingMode,
                context.state.shippingMode === ShippingMode.NATIONWIDE
            );
            if (context.state.shippingType === ShippingType.DELIVERY) {
                if (context.state.shippingMode === ShippingMode.NATIONWIDE) {
                    for (let index = 0; index < scheduleList.length; index++) {
                        scheduleList[index].mode = ShippingMode.NATIONWIDE;
                    }
                }

                if (context.state.shippingMode === ShippingMode.EXPRESS) {
                    for (let index = 0; index < scheduleList.length; index++) {
                        if (scheduleList[index].preOrder == undefined) {
                            scheduleList[index].mode = ShippingMode.EXPRESS;
                        }
                    }
                }
            } else {
                for (let index = 0; index < scheduleList.length; index++) {
                    scheduleList[index].mode = ShippingMode.REGULAR;
                }
            }

            console.log({ scheduleList });

            const shipping: IOrderShipping = {
                scheduleList,
                shippingAddress: addr?.address,
                shippingSubDistrict:
                    addr?.subDistrict == null ? "" : addr?.subDistrict,
                shippingDistrict: addr?.district == null ? "" : addr?.district,
                shippingProvince: addr?.province == null ? "" : addr?.province,
                shippingPostcode: addr?.postcode,
                shippingPhone: addr?.phone,
                shippingEmail: addr?.email,
                shippingFirstName: addr?.firstname,
                shippingLastName: addr?.lastname,
                shippingLat: addr?.location?.lat,
                shippingLon: addr?.location?.lng,
                shippingType: context.state.shippingType?.toString(),
            };

            let ownerId;
            if (customer.phone_number) {
                ownerId = customer.phone_number;
            } else if (customer.email) {
                ownerId = customer.email;
            }

            const sale = (context.rootState as any)["sale"]["sale"];
            console.log({ sale });

            const couponCodeList = context.state.couponCodeList;
            if (context.state.couponCode) {
                couponCodeList.push(context.state.couponCode);
            }

            let productList = context.getters["productList"];

            productList = productList.map((item: any) => {
                return {
                    ...item,
                    rowTotal: item.quantity * item.price,
                }
            });

            // create order class
            const order = new Order({
                ownerId,
                basketId: ownerId,
                branchId: branch["branch"].code,
                productList: productList,
                shipping,
                requestSubstitute: context.state.requestSubstitute,
                noPlasticBag: context.state.noPlasticBags,
                specialComment: context.state.specialComment || "",
                saleId: sale.id,
                couponCodeList,
                voucherId: context.state.voucherId
            });

            console.log("idToken: ", idToken);

            const session = await Auth.currentSession();
            let groups = session.getIdToken().payload["cognito:groups"];
            console.log("groups:", groups);

            groups = groups.filter((g: string) => g != "Admin")
            groups = groups.filter((g: string) => g != "Sale")

            let group = "";

            // this.$services.orders.(, idToken);
            if (groups.length > 0) {
                group = groups[0];
            }
            
            const response = await this.$services.orders.createOrder(
                order.data(),
                idToken!,
                group,
            );

            // return response;
            // TODO book each delivery slots

            // TODO create request to pay
            const rtp = await this.$services.requestToPay.createRequestToPay({
                orderID: response.orderId!,
                expirationTime: dayjs().add(24, "hour").unix(),
                saleID: sale.id,
                customerID: customer.id,
            });

            // fake payment
            // await this.$services.pay.payByOther(response.orderId!);
            context.commit("setPlaceOrderLoading", false);

            return rtp;
        } catch (error) {
            context.commit("setPlaceOrderLoading", false);
            throw error;
        }
    },
};

const checkout: Module<ICheckoutState, ICheckoutState> = {
    namespaced: true,
    state: {
        loading: false,
        ownerId: null,
        shippingType: null,
        shippingAddress: null,
        shippingMode: null,
        couponCode: null,
        scheduleGroups: [],
        specialComment: null,
        noPlasticBags: false,
        requestSubstitute: false,

        // delivery type state
        deliveryTypeLoading: true,
        canRegular: false,
        canExpress: false,
        canNationwide: false,
        branchList: [],

        // order schedule
        scheduleList: [],

        // get cost
        costLoading: false,
        subTotal: null,
        grandTotal: null,
        discountTotal: null,
        promotionalDiscount: null,
        voucherDiscount: null,
        expressFee: null,
        deliveryFee: null,

        // place order
        placeOrderLoading: false,

        // auto apply coupon
        couponCodeList: [],

        // Villa's gift voucher
        voucherId: [],

    },
    getters,
    mutations,
    actions,
};

export default checkout;
